import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Search, Sparkles, Trash2 } from 'lucide-react';
import { Header } from '@/components/layout/Header';
import { PageBody } from '@/components/layout/PageBody';
import { SelectProjectPrompt } from '@/components/projects/SelectProjectPrompt';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ConvergenceBreakdownInline } from '@/components/outcomes/ConvergenceBreakdownCard';
import { useProject } from '@/contexts/ProjectContext';
import { listOutcomes, createOutcome, discoverOutcomes, deleteOutcome } from '@/lib/api';
import type { OutcomeSummary, CodebaseResponse } from '@/lib/api';

const ALL_STATUSES = [
  'all',
  'draft',
  'approved',
  'in-progress',
  'converging',
  'complete',
  'conditional',
  'rework',
] as const;

function statusVariant(status: string): 'secondary' | 'default' | 'destructive' | 'outline' {
  switch (status) {
    case 'draft':
      return 'secondary';
    case 'complete':
      return 'default';
    case 'rework':
      return 'destructive';
    case 'conditional':
      return 'outline';
    default:
      return 'default';
  }
}

function convergenceColor(score: number | null): string {
  if (score === null || score === undefined) return 'text-text-tertiary';
  if (score >= 90) return 'text-success';
  if (score >= 70) return 'text-warning';
  return 'text-error';
}

function formatDate(dateStr: string): string {
  const d = new Date(dateStr);
  return d.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function OutcomesPage(): React.ReactElement {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { selectedProjectId, setSelectedProjectId, codebases, isLoadingCodebases } = useProject();
  const [showCreate, setShowCreate] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');

  const codebase = codebases?.find(cb => cb.id === selectedProjectId);

  const {
    data: outcomes,
    isLoading,
    error: outcomesError,
  } = useQuery({
    queryKey: ['outcomes', selectedProjectId],
    queryFn: () => listOutcomes(selectedProjectId ?? ''),
    enabled: !!selectedProjectId,
  });

  const createMutation = useMutation({
    mutationFn: (title: string) => createOutcome(selectedProjectId ?? '', title),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['outcomes', selectedProjectId] });
      setNewTitle('');
      setShowCreate(false);
    },
  });

  const discoverMutation = useMutation({
    mutationFn: () => discoverOutcomes(selectedProjectId ?? ''),
    onSuccess: result => {
      void navigate(`/chat/${result.conversationId}`);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (outcomeId: string) => deleteOutcome(selectedProjectId ?? '', outcomeId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['outcomes', selectedProjectId] });
    },
  });

  const total = outcomes?.length ?? 0;
  const active = outcomes?.filter((o: OutcomeSummary) => o.status !== 'complete').length ?? 0;
  const completed = outcomes?.filter((o: OutcomeSummary) => o.status === 'complete').length ?? 0;
  const avgScore =
    outcomes && outcomes.length > 0
      ? outcomes.reduce((sum: number, o: OutcomeSummary) => sum + (o.convergenceScore ?? 0), 0) /
        outcomes.filter((o: OutcomeSummary) => o.convergenceScore !== null).length
      : null;

  const filteredOutcomes = outcomes?.filter((o: OutcomeSummary) => {
    if (statusFilter !== 'all' && o.status !== statusFilter) return false;
    if (
      searchQuery &&
      !o.title.toLowerCase().includes(searchQuery.toLowerCase()) &&
      !o.id.toLowerCase().includes(searchQuery.toLowerCase())
    )
      return false;
    return true;
  });

  function handleCreate(e: React.FormEvent): void {
    e.preventDefault();
    if (newTitle.trim()) {
      createMutation.mutate(newTitle.trim());
    }
  }

  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      <Header title="Outcomes" projectName={codebase?.name} />
      <PageBody contentClassName="space-y-6">
        {/* Project selector — always visible */}
        <div className="flex items-center gap-3">
          <label
            htmlFor="outcomes-project-select"
            className="text-sm text-text-secondary whitespace-nowrap"
          >
            Project:
          </label>
          <select
            id="outcomes-project-select"
            value={selectedProjectId ?? ''}
            onChange={(e): void => {
              setSelectedProjectId(e.target.value || null);
            }}
            className="h-9 rounded-md border border-border bg-surface-elevated text-text-primary px-3 text-sm focus:outline-none focus:ring-1 focus:ring-ring flex-1 max-w-md"
          >
            <option value="">Select a project...</option>
            {isLoadingCodebases ? (
              <option disabled>Loading...</option>
            ) : (
              codebases?.map((cb: CodebaseResponse) => (
                <option key={cb.id} value={cb.id}>
                  {cb.name}
                </option>
              ))
            )}
          </select>
        </div>

        {!selectedProjectId ? (
          <SelectProjectPrompt action="view and manage outcomes" />
        ) : outcomesError ? (
          <div
            role="alert"
            className="rounded-md border border-error/30 bg-error/10 p-4 text-sm text-error"
          >
            Failed to load outcomes: {outcomesError.message}
          </div>
        ) : (
          <>
            {/* Summary cards */}
            <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
              <Card>
                <CardContent className="pt-4">
                  <div className="text-2xl font-bold text-text-primary">{total}</div>
                  <div className="text-xs text-muted-foreground">Total Outcomes</div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4">
                  <div className="text-2xl font-bold text-text-primary">{active}</div>
                  <div className="text-xs text-muted-foreground">Active</div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4">
                  <div className="text-2xl font-bold text-success">{completed}</div>
                  <div className="text-xs text-muted-foreground">Completed</div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4">
                  <div
                    className={`text-2xl font-bold ${avgScore !== null && !isNaN(avgScore) ? convergenceColor(avgScore) : 'text-text-tertiary'}`}
                  >
                    {avgScore !== null && !isNaN(avgScore) ? `${Math.round(avgScore)}%` : '--'}
                  </div>
                  <div className="text-xs text-muted-foreground">Avg Convergence</div>
                </CardContent>
              </Card>
            </div>

            {/* Filter bar */}
            <div className="flex items-center gap-3 flex-wrap">
              <div className="flex items-center gap-1">
                {ALL_STATUSES.map(s => (
                  <Button
                    key={s}
                    size="sm"
                    variant={statusFilter === s ? 'default' : 'ghost'}
                    onClick={() => {
                      setStatusFilter(s);
                    }}
                    className="h-7 text-xs capitalize"
                  >
                    {s}
                  </Button>
                ))}
              </div>
              <div className="relative flex-1 min-w-[200px] max-w-xs">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-text-tertiary" />
                <Input
                  value={searchQuery}
                  onChange={e => {
                    setSearchQuery(e.target.value);
                  }}
                  placeholder="Search outcomes..."
                  className="h-8 pl-8 text-sm"
                />
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-medium text-text-primary">
                {filteredOutcomes?.length ?? 0} Outcome
                {(filteredOutcomes?.length ?? 0) !== 1 ? 's' : ''}
                {statusFilter !== 'all' && (
                  <span className="text-text-tertiary ml-1">({statusFilter})</span>
                )}
              </h2>
              {showCreate ? (
                <form onSubmit={handleCreate} className="flex items-center gap-2">
                  <Input
                    value={newTitle}
                    onChange={e => {
                      setNewTitle(e.target.value);
                    }}
                    placeholder="Outcome title..."
                    className="h-8 w-64 text-sm"
                    autoFocus
                  />
                  <Button
                    type="submit"
                    size="sm"
                    disabled={createMutation.isPending || !newTitle.trim()}
                  >
                    {createMutation.isPending ? 'Creating...' : 'Create'}
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setShowCreate(false);
                      setNewTitle('');
                    }}
                  >
                    Cancel
                  </Button>
                </form>
              ) : (
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      discoverMutation.mutate();
                    }}
                    disabled={discoverMutation.isPending}
                  >
                    <Sparkles className="mr-1 h-4 w-4" />
                    {discoverMutation.isPending ? 'Starting...' : 'Discover Outcomes'}
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => {
                      setShowCreate(true);
                    }}
                  >
                    <Plus className="mr-1 h-4 w-4" />
                    Create Outcome
                  </Button>
                </div>
              )}
            </div>

            {discoverMutation.isError && (
              <div className="text-sm text-error">
                Failed to start discovery:{' '}
                {discoverMutation.error instanceof Error
                  ? discoverMutation.error.message
                  : 'Unknown error'}
              </div>
            )}

            {createMutation.isError && (
              <div className="text-sm text-error">
                Failed to create outcome:{' '}
                {createMutation.error instanceof Error
                  ? createMutation.error.message
                  : 'Unknown error'}
              </div>
            )}

            {/* Table */}
            {isLoading ? (
              <div className="text-sm text-muted-foreground">Loading outcomes...</div>
            ) : !filteredOutcomes || filteredOutcomes.length === 0 ? (
              <div className="text-sm text-muted-foreground">
                {outcomes && outcomes.length > 0
                  ? 'No outcomes match the current filter.'
                  : 'No outcomes yet.'}
              </div>
            ) : (
              <div className="rounded-md border border-border overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border bg-surface-elevated">
                      <th className="px-4 py-2 text-left font-medium text-text-secondary">ID</th>
                      <th className="px-4 py-2 text-left font-medium text-text-secondary">Title</th>
                      <th className="px-4 py-2 text-left font-medium text-text-secondary">
                        Status
                      </th>
                      <th className="px-4 py-2 text-left font-medium text-text-secondary">
                        Convergence
                      </th>
                      <th className="px-4 py-2 text-left font-medium text-text-secondary">
                        Updated
                      </th>
                      <th className="px-4 py-2 text-right font-medium text-text-secondary">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredOutcomes.map((o: OutcomeSummary) => (
                      <tr
                        key={o.id}
                        className="border-b border-border hover:bg-surface-elevated/50 cursor-pointer transition-colors"
                        onClick={() => {
                          void navigate(`/outcomes/${o.id}`);
                        }}
                      >
                        <td className="px-4 py-2 font-mono text-xs text-text-secondary">
                          {o.id.slice(0, 8)}
                        </td>
                        <td className="px-4 py-2 text-text-primary">{o.title}</td>
                        <td className="px-4 py-2">
                          <Badge variant={statusVariant(o.status)}>{o.status}</Badge>
                        </td>
                        <td
                          className={`px-4 py-2 font-medium ${convergenceColor(o.convergenceScore)}`}
                        >
                          <div>{o.convergenceScore !== null ? `${o.convergenceScore}%` : '--'}</div>
                          <ConvergenceBreakdownInline
                            assessment={o.convergenceAssessment}
                            availability={o.convergenceAssessmentAvailability}
                          />
                        </td>
                        <td className="px-4 py-2 text-text-secondary text-xs">
                          {formatDate(o.updatedAt)}
                        </td>
                        <td className="px-4 py-2 text-right">
                          <Button
                            size="sm"
                            variant="ghost"
                            aria-label={`Delete ${o.title}`}
                            className="h-6 w-6 p-0 text-text-tertiary hover:text-error"
                            onClick={(e): void => {
                              e.stopPropagation();
                              deleteMutation.mutate(o.id);
                            }}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </PageBody>
    </div>
  );
}
